import React from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const SERVICES = ["Consultation & measure", "Flooring installation", "Tile installation", "Repair visit", "Custom design consult"];
const TIMES = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];

export default function Booking() {
  const [date, setDate] = React.useState();
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", address: "", service: "", time_slot: "", notes: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const disabled = (d) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return d < today || d.getDay() === 0; // no past & no Sundays
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please select a date."); return; }
    setSubmitting(true);
    try {
      const iso = date.toISOString().slice(0,10);
      await api.post("/bookings", { ...form, booking_date: iso });
      setDone(true);
      toast.success("Booking received — we&apos;ll confirm shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="eyebrow text-[color:var(--brand-wood)]">Booking Received</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl">See you soon.</h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">A confirmation email is on its way to you and our team.</p>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="eyebrow text-[color:var(--brand-wood)]">Book installation</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Pick a date. We&apos;ll take care of the rest.
        </h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">
          Consultations and installations available Monday through Saturday.
        </p>
      </div>

      <form onSubmit={submit} className="mt-12 grid gap-10 lg:grid-cols-12" data-testid="booking-form">
        <div className="lg:col-span-5">
          <Label className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">Select date</Label>
          <div className="mt-3 rounded-2xl border border-black/10 bg-white p-3 inline-block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disabled}
              data-testid="booking-calendar"
            />
          </div>
          {date && (
            <div data-testid="booking-selected-date" className="mt-4 text-sm text-[color:var(--brand-woodDark)]">
              Selected: <span className="text-[color:var(--brand-ink)] font-medium">{date.toDateString()}</span>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 grid gap-5 sm:grid-cols-2">
          <Field label="Full name" htmlFor="b-name">
            <Input id="b-name" required data-testid="booking-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
          </Field>
          <Field label="Phone" htmlFor="b-phone">
            <Input id="b-phone" required data-testid="booking-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/>
          </Field>
          <Field label="Email" htmlFor="b-email">
            <Input id="b-email" type="email" required data-testid="booking-email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/>
          </Field>
          <Field label="Address" htmlFor="b-address">
            <Input id="b-address" required data-testid="booking-address" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/>
          </Field>
          <Field label="Service" htmlFor="b-service" className="sm:col-span-2">
            <Select value={form.service} onValueChange={(v)=>setForm({...form, service:v})}>
              <SelectTrigger id="b-service" data-testid="booking-service"><SelectValue placeholder="Choose a service"/></SelectTrigger>
              <SelectContent>
                {SERVICES.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Preferred time" htmlFor="b-time" className="sm:col-span-2">
            <Select value={form.time_slot} onValueChange={(v)=>setForm({...form, time_slot:v})}>
              <SelectTrigger id="b-time" data-testid="booking-time"><SelectValue placeholder="Pick a time"/></SelectTrigger>
              <SelectContent>
                {TIMES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Notes (optional)" htmlFor="b-notes" className="sm:col-span-2">
            <Textarea id="b-notes" rows={3} data-testid="booking-notes" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/>
          </Field>

          <div className="sm:col-span-2 flex items-center justify-between mt-2">
            <p className="text-xs text-[color:var(--brand-woodDark)]">Confirmations sent to <span className="text-[color:var(--brand-ink)]">info@jfeva.com</span></p>
            <button type="submit" disabled={submitting || !date} data-testid="booking-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-3 text-sm font-medium disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
              Book my installation
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, htmlFor, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
